import React, { useState, useEffect } from 'react';
import { useEditorStore } from '../../stores';
import useTrimlineStore from '../../stores/useTrimlineStore';
import styles from './ToolCard.module.css';
import NumberInput from '../ui/NumberInput';
import { useToolManager } from '../../hooks/useToolManager';
import { TOOLS_CONFIG } from '../../constants/tools';

const TrimlineToolCard = () => {
  const trimlineState = useTrimlineStore((s) => s.trimline);
  const previewMesh = useTrimlineStore((s) => s.previewMesh);
  const selectPoint = useTrimlineStore((s) => s.selectPoint);
  const updatePoint = useTrimlineStore((s) => s.updatePoint);
  const mirrorLeftToRight = useTrimlineStore((s) => s.mirrorLeftToRight);
  const setPreviewVisible = useTrimlineStore((s) => s.setPreviewVisible);
  const reset = useTrimlineStore((s) => s.reset);
  const requestReinitialize = useTrimlineStore((s) => s.requestReinitialize);

  const undoCommand = useTrimlineStore((state) => state.currentTrimlineCommand);
  const isApplied = useTrimlineStore((state) => state.applied);
  const isAppliedFromDraft = useTrimlineStore((state) => state.isAppliedFromDraft);

  const { run } = useToolManager();

  const selectedPointIndex = trimlineState.selectedPointIndex;
  const isLoadedFromDraft = isApplied && isAppliedFromDraft;
  const selectedPoint = selectedPointIndex !== null ? trimlineState.points[selectedPointIndex] : null;

  const handleTogglePreview = () => {
    setPreviewVisible(!trimlineState.previewVisible);
  };

  const handleInitializePoints = () => {
    selectPoint(null);
    requestReinitialize();
  };

  const handleApplySymmetry = () => {
    mirrorLeftToRight();
  };

  const handleApplyTrimline = async () => {
    if (isApplied || !trimlineState.enabled) return;

    console.log('[TrimlineToolCard] Apply button clicked - checking if recomputation is required');

    const { setIsLoading } = useEditorStore.getState();

    try {
      // Show loading overlay while applying
      setIsLoading(true, 'Applying Trimline');

      // **CRITICAL: Check if recomputation is required and force rebuild before applying**
      // Similar to CarveReliefHandler - ensure preview is rebuilt with latest curve state
      const { isRecomputationRequired, rebuildPreviewImmediate } = useTrimlineStore.getState();
      
      if (isRecomputationRequired && isRecomputationRequired()) {
        console.log('[TrimlineToolCard] Curve changed - forcing recomputation before apply');
        
        // Force immediate recomputation (bypasses debouncing and visibility checks)
        if (rebuildPreviewImmediate) {
          await rebuildPreviewImmediate();
          
          // Wait for worker to complete by polling
          const { workerLoading } = useEditorStore.getState();
          if (workerLoading) {
            await new Promise((resolve) => {
              let checks = 0;
              const maxChecks = 120; // 60 seconds timeout
              
              const checkCompletion = () => {
                checks++;
                const currentState = useEditorStore.getState();
                
                if (!currentState.workerLoading || checks >= maxChecks) {
                  resolve();
                } else {
                  setTimeout(checkCompletion, 500);
                }
              };
              
              setTimeout(checkCompletion, 100);
            });
          }
        }
      }

      // Verify preview mesh exists before applying
      const latestPreview = useTrimlineStore.getState().previewMesh;
      if (!latestPreview) {
        console.error('[TrimlineToolCard] No preview mesh available after recomputation');
        return;
      }

      console.log('[TrimlineToolCard] Applying trimline with computed preview');
      run({ toolId: TOOLS_CONFIG.MAIN_PROCESS.TRIMLINE.main_trimline_trimline.id, mode: 'apply' });
    } finally {
      // Clear loading state
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    selectPoint(null);
    reset();
  };

  const handleCoordinateChange = (axis, value) => {
    if (selectedPointIndex === null || !selectedPoint) return;
    
    const numValue = Math.round((parseFloat(value) || 0) * 100) / 100;
    
    if (axis === 'x') {
      updatePoint(selectedPointIndex, { ...selectedPoint, x: numValue });
    } else if (axis === 'y') {
      updatePoint(selectedPointIndex, { ...selectedPoint, y: numValue });
    } else if (axis === 'z') {
      updatePoint(selectedPointIndex, { ...selectedPoint, z: numValue });
    }
  };

  const unapplyTrimline = () => {
    if (undoCommand) {
      // Normal unapply with command history
      const { resetHistoryUpTo } = useEditorStore.getState();
      resetHistoryUpTo(undoCommand);
      undoCommand.undo();
    } else {
      // Unapply from draft - need to reset states manually
      const { setIsTrimlineApplied, setApplied, reset } = useTrimlineStore.getState();
      setIsTrimlineApplied(false);
      setApplied(false);

      // Reset the isAppliedFromDraft flag
      useTrimlineStore.setState({ isAppliedFromDraft: false });

      // Reset to initial state
      reset();
    }
  };

  return (
    <div className='flex flex-col absolute left-[10px] top-[10px] gap-2'>
      <div className={styles.toolCard} style={{ position: 'relative', left: '0px', top: '0px'}}>
        <div className={styles.toolCardHeader}>
          <h3>Trimline</h3>
          {!isApplied && <p>Configure 6-point cubic bezier trimline</p>}
          {isApplied && !isLoadedFromDraft && <p>☑️ Trimline Applied</p>}
          {isApplied && isLoadedFromDraft && <p>☑️ Trimline Applied from Draft</p>}
        </div>
        
        {/* Main container with subtle background and border */}
        <div className='rounded-lg border border-slate-200 bg-slate-50 p-4'>
          <div className={`flex flex-col gap-4 ${isApplied ? 'opacity-30 select-none pointer-events-none' : ''}`}>
            
            {/* Selected Point Coordinates */}
            {selectedPointIndex !== null && selectedPoint && (
              <div className='relative flex flex-col gap-2'>
                <h4 className='text-sm font-medium text-gray-800'>
                  Point {selectedPointIndex + 1}
                </h4>
                <div className='flex flex-col bg-white p-3 rounded border'>
                  <div className='text-xs text-gray-600 mb-2'>Point Position:</div>
                  <div className='flex flex-col gap-2'>
                    <div>
                      {/* <label className='text-xs text-gray-500'>X:</label> */}
                      <NumberInput
                        value={selectedPoint.x || '0.00'}
                        onChange={(value) => handleCoordinateChange('x', value)}
                        className='w-full text-xs'
                        disabled={isApplied}
                        min={-1000}
                        max={1000}
                        step={0.01}
                        label='X'
                        decimals={2}
                      />
                    </div>
                    <div>
                      {/* <label className='text-xs text-gray-500'>Y:</label> */}
                      <NumberInput
                        value={selectedPoint.y || '0.00'}
                        onChange={(value) => handleCoordinateChange('y', value)}
                        className='w-full text-xs'
                        disabled={isApplied}
                        min={-1000}
                        max={1000}
                        step={0.01}
                        label='Y'
                        decimals={2}
                      />
                    </div>
                    <div>
                      {/* <label className='text-xs text-gray-500'>Z:</label> */}
                      <NumberInput
                        value={selectedPoint.z || '0.00'}
                        onChange={(value) => handleCoordinateChange('z', value)}
                        className='w-full text-xs'
                        disabled={isApplied}
                        min={-1000}
                        max={1000}
                        step={0.01}
                        label='Z'
                        decimals={2}
                      />
                    </div>
                  </div>
                  
                  {trimlineState.bezierHandles && trimlineState.bezierHandles[selectedPointIndex] && (
                    <div className='flex flex-col mt-4'>
                      <div className='text-xs text-gray-600 mb-1'>Bezier Handles:</div>
                      <div className='text-xs text-gray-500'>
                        In: ({trimlineState.bezierHandles[selectedPointIndex].in.x.toFixed(2)}, {trimlineState.bezierHandles[selectedPointIndex].in.y.toFixed(2)}, {trimlineState.bezierHandles[selectedPointIndex].in.z.toFixed(2)})
                      </div>
                      <div className='text-xs text-gray-500'>
                        Out: ({trimlineState.bezierHandles[selectedPointIndex].out.x.toFixed(2)}, {trimlineState.bezierHandles[selectedPointIndex].out.y.toFixed(2)}, {trimlineState.bezierHandles[selectedPointIndex].out.z.toFixed(2)})
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {!selectedPoint && (
              <div className='text-sm text-gray-600 text-center p-2'>
                Click on a point to select and view coordinates
              </div>
            )}

            {/* Separator */}
            <hr className='border-slate-200' />

            {/* Action Buttons */}
            <div className='flex flex-col gap-2'>
              <button
                onClick={handleInitializePoints}
                className='px-3 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors hover:cursor-pointer'
              >
                Initialize Points
              </button>
              <button
                onClick={handleApplySymmetry}
                className='px-3 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors hover:cursor-pointer'
              >
                Apply Symmetry
              </button>
              <button
                onClick={handleReset}
                className='px-3 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors hover:cursor-pointer'
              >
                Reset
              </button>
            </div>

            {/* Usage Information */}
            <div className='text-xs text-gray-500 bg-gray-100 p-2 rounded'>
              <div>• Click points to select and drag</div>
              <div>• Hold and drag to move points and handles</div>
              <div>• Curve projects to mesh surface</div>
              <div>• Cut keeps only bottom portion</div>
            </div>
          </div>
        </div>
      </div>

      {!isApplied && (
        <div className='flex items-center justify-between mt-1 px-2'>
          <div className="flex items-center cursor-pointer select-none ml-1" onClick={handleTogglePreview}>
            <div className={`w-10 h-6 rounded-full transition-colors duration-200 relative ${trimlineState.previewVisible ? 'bg-purple-600' : 'bg-gray-300'}`}>
              <div
                className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 z-10 ${trimlineState.previewVisible ? 'translate-x-4' : 'translate-x-0'}`}
              ></div>
            </div>
            <span className={`ml-3 text-sm font-medium ${trimlineState.previewVisible ? 'text-gray-700' : 'text-gray-700'}`}>
              Show Preview
            </span>
          </div>

          <button
            className={`px-2 py-1 rounded-md ml-2 min-w-22 text-white text-lg cursor-pointer bg-violet-600 hover:text-white hover:bg-violet-500`}
            onClick={handleApplyTrimline}
          >
            Apply
          </button>
        </div>
      )}

      {isApplied && (
        <button
          className={`px-2 py-1 self-end rounded-md w-25 mt-1 text-white ${
            isLoadedFromDraft
              ? 'bg-gray-400 cursor-not-allowed opacity-60'
              : 'bg-red-400 cursor-pointer hover:bg-red-500 hover:text-white'
          }`}
          onClick={isLoadedFromDraft ? undefined : unapplyTrimline}
          disabled={isLoadedFromDraft}
          title={isLoadedFromDraft ? "Cannot unapply trimline loaded from draft" : "Unapply trimline"}
        >
          Unapply
        </button>
      )}
    </div>
  );
};

export default TrimlineToolCard; 